import type { ReactNode } from 'react';
import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ActivityTimeline } from '../../components/ActivityTimeline';
import { NotesSection } from '../../components/NotesSection';
import { ConvertToOpportunityModal } from '../../components/ConvertToOpportunityModal';
import {
  useLeadDetail,
  statusColors,
  statusLabels,
  sourceLabels,
  getScoreColor,
  formatLeadDate,
} from '../../hooks/useLeads';
import type { LeadStatus } from '../../types';

interface EditLeadModalProps {
  lead: NonNullable<ReturnType<typeof useLeadDetail>['lead']>;
  onSave: (updates: Parameters<ReturnType<typeof useLeadDetail>['updateLead']>[0]) => Promise<unknown>;
  onClose: () => void;
}

function EditLeadModal({ lead, onSave, onClose }: EditLeadModalProps): ReactNode {
  const [score, setScore] = useState(lead.score.toString());
  const [assignedTo, setAssignedTo] = useState(lead.assigned_to || '');
  const [source, setSource] = useState(lead.source);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await onSave({
      score: parseInt(score, 10),
      assigned_to: assignedTo || undefined,
      source,
    });
    setSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Lead</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Score (0-100)
              </label>
              <input
                type="number"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                min="0"
                max="100"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as typeof source)}
                className="w-full"
              >
                {Object.entries(sourceLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned To
              </label>
              <input
                type="text"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                placeholder="Enter assignee"
                className="w-full"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="btn btn-primary">
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ChangeStatusModalProps {
  currentStatus: LeadStatus;
  onChangeStatus: (status: LeadStatus) => Promise<unknown>;
  onClose: () => void;
}

function ChangeStatusModal({ currentStatus, onChangeStatus, onClose }: ChangeStatusModalProps): ReactNode {
  const [status, setStatus] = useState<LeadStatus>(currentStatus);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === currentStatus) {
      onClose();
      return;
    }
    setSubmitting(true);
    await onChangeStatus(status);
    setSubmitting(false);
    onClose();
  };

  // Filter out converted status - that's done via convert action
  const availableStatuses = Object.entries(statusLabels).filter(
    ([value]) => value !== 'converted'
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Status</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-2">
            {availableStatuses.map(([value, label]) => (
              <label
                key={value}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                  status === value
                    ? 'border-ds-primary bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="status"
                  value={value}
                  checked={status === value}
                  onChange={(e) => setStatus(e.target.value as LeadStatus)}
                  className="sr-only"
                />
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[value as LeadStatus]}`}>
                  {label}
                </span>
                {value === currentStatus && (
                  <span className="ml-auto text-xs text-gray-500">Current</span>
                )}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="btn btn-primary">
              {submitting ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  leadName: string;
  onDelete: () => Promise<boolean>;
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteConfirmModal({ leadName, onDelete, onClose, onDeleted }: DeleteConfirmModalProps): ReactNode {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    const success = await onDelete();
    setDeleting(false);
    if (success) {
      onDeleted();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Lead</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete <strong>{leadName}</strong>? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary" disabled={deleting}>
            Cancel
          </button>
          <button onClick={handleDelete} className="btn btn-danger" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete Lead'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function LeadDetail(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    lead,
    loading,
    error,
    updateLead,
    deleteLead,
    qualifyLead,
    unqualifyLead,
    convertLead,
  } = useLeadDetail(id || '');

  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  if (loading) {
    return (
      <div className="page-container">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-24 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="card h-48"></div>
              <div className="card h-64"></div>
            </div>
            <div className="space-y-4">
              <div className="card h-32"></div>
              <div className="card h-48"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="page-container">
        <div className="mb-4">
          <Link to="/leads" className="text-ds-primary hover:underline text-sm">
            ← Back to Leads
          </Link>
        </div>
        <div className="card text-center py-12">
          <p className="text-gray-500 mb-4">{error || 'Lead not found'}</p>
          <Link to="/leads" className="btn btn-primary">
            Go to Leads
          </Link>
        </div>
      </div>
    );
  }

  const leadName = lead.metadata?.name || 'Unknown Lead';
  const isConverted = lead.status === 'converted';
  const isQualified = lead.status === 'qualified';

  const handleChangeStatus = async (status: LeadStatus) => {
    if (status === 'qualified') {
      await qualifyLead();
    } else if (status === 'unqualified') {
      await unqualifyLead();
    } else {
      await updateLead({ status });
    }
  };

  return (
    <div className="page-container">
      <div className="mb-4">
        <Link to="/leads" className="text-ds-primary hover:underline text-sm">
          ← Back to Leads
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Header Card */}
          <div className="card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-xl font-semibold text-gray-900">{leadName}</h1>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[lead.status]}`}>
                    {statusLabels[lead.status]}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className={`font-semibold ${getScoreColor(lead.score)}`}>
                    Score: {lead.score}
                  </span>
                  {lead.metadata?.company && (
                    <span>{lead.metadata.company}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="btn btn-secondary"
                  disabled={isConverted}
                >
                  Edit
                </button>
                {isQualified && !isConverted && (
                  <button
                    onClick={() => setShowConvertModal(true)}
                    className="btn btn-primary"
                  >
                    Convert to Opportunity
                  </button>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-gray-900">
                  {lead.metadata?.email ? (
                    <a href={`mailto:${lead.metadata.email}`} className="text-ds-primary hover:underline">
                      {lead.metadata.email}
                    </a>
                  ) : '—'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Phone</label>
                <p className="text-gray-900">
                  {lead.metadata?.phone ? (
                    <a href={`tel:${lead.metadata.phone}`} className="text-ds-primary hover:underline">
                      {lead.metadata.phone}
                    </a>
                  ) : '—'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Company</label>
                <p className="text-gray-900">{lead.metadata?.company || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Title</label>
                <p className="text-gray-900">{lead.metadata?.title || '—'}</p>
              </div>
            </div>

            {/* Lead Info */}
            <div className="grid grid-cols-2 gap-4 pt-4 mt-4 border-t">
              <div>
                <label className="text-sm font-medium text-gray-500">Source</label>
                <p className="text-gray-900">{sourceLabels[lead.source]}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Created</label>
                <p className="text-gray-900">{formatLeadDate(lead.created_at)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Assigned To</label>
                <p className="text-gray-900">{lead.assigned_to || '—'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Last Updated</label>
                <p className="text-gray-900">{formatLeadDate(lead.updated_at)}</p>
              </div>
            </div>

            {/* Converted notice */}
            {isConverted && lead.converted_to_opportunity_id && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-purple-700 font-medium">
                  ✨ This lead has been converted to an opportunity
                </p>
                <Link
                  to={`/opportunities/${lead.converted_to_opportunity_id}`}
                  className="text-purple-600 hover:underline text-sm"
                >
                  View Opportunity →
                </Link>
              </div>
            )}
          </div>

          {/* Activities */}
          {id && (
            <ActivityTimeline
              parentType="lead"
              parentId={id}
              showAddButton={!isConverted}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Actions Card */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Actions</h2>
            <div className="space-y-2">
              <button
                onClick={() => setShowStatusModal(true)}
                className="btn btn-secondary w-full"
                disabled={isConverted}
              >
                Change Status
              </button>
              {!isQualified && !isConverted && lead.status !== 'unqualified' && (
                <button
                  onClick={() => qualifyLead()}
                  className="btn btn-primary w-full"
                >
                  Qualify Lead
                </button>
              )}
              {isQualified && !isConverted && (
                <button
                  onClick={() => setShowConvertModal(true)}
                  className="btn btn-primary w-full"
                >
                  Convert to Opportunity
                </button>
              )}
              <button
                onClick={() => setShowDeleteModal(true)}
                className="btn btn-danger w-full"
                disabled={isConverted}
              >
                Delete Lead
              </button>
            </div>
          </div>

          {/* Notes */}
          {id && (
            <NotesSection
              parentType="lead"
              parentId={id}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditLeadModal
          lead={lead}
          onSave={updateLead}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showStatusModal && (
        <ChangeStatusModal
          currentStatus={lead.status}
          onChangeStatus={handleChangeStatus}
          onClose={() => setShowStatusModal(false)}
        />
      )}

      {showConvertModal && (
        <ConvertToOpportunityModal
          leadId={lead.id}
          leadName={leadName}
          onConvert={convertLead}
          onClose={() => setShowConvertModal(false)}
          onSuccess={(oppId) => navigate(`/opportunities/${oppId}`)}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmModal
          leadName={leadName}
          onDelete={deleteLead}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => navigate('/leads')}
        />
      )}
    </div>
  );
}

export default LeadDetail;
